import React, { useState } from "react";
import { Link } from "react-router-dom";
import AsyncSelect from "react-select/async";
import toast from "react-hot-toast";
import { Link as LinkIcon, X } from "react-feather";
import {
  useGetLinkedCustomersQuery,
  useLazySearchCustomersAllBranchesQuery,
  useLinkCustomerMutation,
  useUnlinkCustomerMutation,
} from "../customersApi";
import { paths } from "../../../../routes/paths";

// "Linked in other branches" panel (handoff §9.7/§13.2) - a customer doing
// business with more than one branch keeps fully independent records per
// branch (own guard assignments, own everything); this is a purely
// identity-level cross-reference on top, never touching operational data.
const LinkedCustomersPanel = ({ branchId, custId, readOnly }) => {
  const [showSearch, setShowSearch] = useState(false);
  const { data: linked = [], isLoading } = useGetLinkedCustomersQuery({ branchId, custId });
  const [triggerSearch] = useLazySearchCustomersAllBranchesQuery();
  const [linkCustomer, { isLoading: isLinking }] = useLinkCustomerMutation();
  const [unlinkCustomer] = useUnlinkCustomerMutation();

  const loadOptions = async (inputValue) => {
    if (!inputValue || inputValue.trim().length < 2) return [];
    const { data = [] } = await triggerSearch(inputValue.trim());
    return data
      .filter((c) => !(c.branchId === Number(branchId) && c.custId === Number(custId)))
      .filter((c) => !linked.some((l) => l.branchId === c.branchId && l.custId === c.custId))
      .map((c) => ({
        value: `${c.branchId}-${c.custId}`,
        label: `${c.custName} · ${c.branchCode}${c.contactPerson ? ` · ${c.contactPerson}` : ""}`,
        customer: c,
      }));
  };

  const handleLink = async (option) => {
    if (!option) return;
    try {
      await linkCustomer({
        branchId,
        custId,
        targetBranchId: option.customer.branchId,
        targetCustId: option.customer.custId,
      }).unwrap();
      toast.success(`Linked to ${option.customer.custName} (${option.customer.branchCode})`);
      setShowSearch(false);
    } catch (error) {
      toast.error(error?.message || "Could not link customer");
    }
  };

  const handleUnlink = async (sibling) => {
    if (!window.confirm(`Unlink from ${sibling.custName} (${sibling.branchCode})?`)) return;
    try {
      await unlinkCustomer({ branchId, custId }).unwrap();
      toast.success("Unlinked");
    } catch (error) {
      toast.error(error?.message || "Could not unlink");
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Linked in Other Branches</h5>
          {!readOnly && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => setShowSearch((v) => !v)}
            >
              <LinkIcon size={14} className="me-1" />
              {showSearch ? "Cancel" : "Link to another branch"}
            </button>
          )}
        </div>

        {showSearch && (
          <div className="mb-3">
            <AsyncSelect
              classNamePrefix="react-select"
              cacheOptions
              loadOptions={loadOptions}
              onChange={handleLink}
              isLoading={isLinking}
              placeholder="Search by name, phone, or contact (all branches)..."
              noOptionsMessage={({ inputValue }) =>
                inputValue.length < 2 ? "Type at least 2 characters" : "No matching customers"
              }
            />
          </div>
        )}

        {isLoading ? (
          <p className="text-muted mb-0">Loading...</p>
        ) : linked.length === 0 ? (
          <p className="text-muted mb-0">
            Not linked to any other branch's customer record.
          </p>
        ) : (
          <div className="row">
            {linked.map((sibling) => (
              <div className="col-md-6 mb-2" key={`${sibling.branchId}-${sibling.custId}`}>
                <div className="border rounded p-2 d-flex justify-content-between align-items-start">
                  <div>
                    <Link to={paths.customerEdit(sibling.branchId, sibling.custId)}>
                      <strong>{sibling.custName}</strong>
                    </Link>
                    <div className="text-muted small">{sibling.branchCode}</div>
                    {sibling.contactPerson && (
                      <div className="text-muted small">{sibling.contactPerson}</div>
                    )}
                    {(sibling.telephone || sibling.mobileNo) && (
                      <div className="text-muted small">
                        {sibling.telephone || sibling.mobileNo}
                      </div>
                    )}
                  </div>
                  {!readOnly && (
                    <button
                      type="button"
                      className="btn btn-link text-danger p-1"
                      title="Unlink"
                      onClick={() => handleUnlink(sibling)}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkedCustomersPanel;
